import React from "react"
import { Form, useNavigate, useSearchParams } from "react-router-dom"

import { captureFromUrl } from "../lib/signupIntent"

import {
  Alert,
  Button,
  Checkbox,
  Heading,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  Text,
  InputGroup,
  InputRightAddon,
  IconButton,
  Tooltip,
} from "@chakra-ui/react"

import { UserRegister } from "../lib/user"
import { onChange } from "../lib/util"
import TOS from "./TOS"
import { GoogleLogin } from "@react-oauth/google"
import { Api } from "../lib/api"

import GradientBox from "./GradientBox"
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons"

export default function Register() {
  const api = new Api()
  const [username, setUsername] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [error, setError] = React.useState(null)
  const [success, setSuccess] = React.useState(null)
  const [inputType, setInputType] = React.useState("password")
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Capture marketing-side ?tier=&billing=&src= params into short-lived
  // localStorage so ChoosePlan can recover them when activation opens a new tab.
  // UTMs are captured by GA4 directly from the URL on every page load.
  React.useEffect(() => {
    captureFromUrl(searchParams)
  }, [searchParams])

  const handleRegister = async (event) => {
    event.preventDefault()

    const { email, password, fullName } = event.target.elements
    let success
    try {
      success = await UserRegister(email.value, password.value, fullName.value)
    } catch (e) {
      success = false
    }

    if (!success) {
      setError("Registration failed")
      setSuccess(null)
    } else {
      console.log("Registration success")
      setError(null)
      setSuccess(
        "Registration successful. Please check your email for an activation link.",
      )
      navigate("/accounts/register-confirm")
    }
  }
  const [open, setOpen] = React.useState(false)
  const [TOSState, setTOSState] = React.useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setTOSState(!TOSState)
  }

  const handleCheckboxChange = (event) => {
    setTOSState(event.target.checked)
  }

  const handleConfirmedClose = () => {
    setOpen(false)
    setTOSState(true)
  }

  const redirectToLogin = () => {
    navigate("/login")
  }

  const responseMessage = async (response) => {
    console.log(response)
    await api.googleLogin(response.credential, response.clientId)
  }

  const errorMessage = (error) => {
    console.log(error)
    setError(error)
  }

  const toggleVisibility = () => {
    setInputType(inputType === "password" ? "text" : "password")
  }

  return (
    <GradientBox>
      <VStack
        justifyContent="flex-start"
        alignItems="left"
        w="fill-available"
        gap={2}
      >
        <Form onSubmit={handleRegister}>
          <Heading variant="standard">Create your account</Heading>
          <Text variant="subtext">
            After email verification, choose a plan to get started
          </Text>
          {error && <Alert status="error">{error}</Alert>}
          {success && <Alert status="success">{success}</Alert>}
          <VStack
            mt={{ base: 3, md: 8 }}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            minWidth="100%"
            spacing="2"
          >
            <Input
              variant="account"
              placeholder="Email"
              autoComplete="username"
              width="100%"
              name="email"
              type="email"
              onChange={onChange(setUsername)}
              value={username}
              status={error && "error"}
              isRequired
            />
            <Text variant="subtext">{error || ""}</Text>
            <InputGroup variant="account">
              <Input
                placeholder="Password"
                autoComplete="password"
                name="password"
                type={inputType}
                onChange={onChange(setPassword)}
                status={error && "error"}
                value={password}
                isRequired
              />
              <InputRightAddon>
                <Tooltip
                  label={
                    inputType === "password" ? "Show password" : "Hide password"
                  }
                >
                  <IconButton
                    variant="link"
                    icon={
                      inputType === "password" ? <ViewIcon /> : <ViewOffIcon />
                    }
                    onClick={toggleVisibility}
                  />
                </Tooltip>
              </InputRightAddon>
            </InputGroup>
            <Text variant="subtext">{error || " "}</Text>
            <Input
              variant="account"
              placeholder="Full Name"
              name="fullName"
              status={error && "error"}
              isRequired
            />
            <Text variant="subtext">{error || " "}</Text>
            <Checkbox
              name="tos"
              isRequired
              isChecked={TOSState}
              onChange={handleCheckboxChange}
              variant="standard"
            >
              <Text variant="subtext">
                I agree to the{" "}
                <Link onClick={handleOpen} textDecoration={"underline"}>
                  Terms of Service.
                </Link>
              </Text>
            </Checkbox>
            <Checkbox name="robot" variant="standard" isRequired>
              <Text variant="subtext"> I am not a robot.</Text>
            </Checkbox>
          </VStack>
          <VStack spacing={1} mt={5}>
            <Button variant="account" type="submit" id="submit">
              Create Account
            </Button>
            <Text variant="subtext"> &mdash; OR &mdash; </Text>
            <GoogleLogin
              onSuccess={responseMessage}
              onError={errorMessage}
              shape="pill"
              text="signup_with"
              context="signup"
              ux_mode="popup"
            />
          </VStack>
        </Form>
        <Text mt={4} variant="subtext" textAlign={"center"}>
          Already a user?{" "}
          <Link onClick={redirectToLogin} textDecoration={"underline"}>
            Sign In
          </Link>
        </Text>
      </VStack>
      <Modal isOpen={open} onClose={handleClose} variant="standard">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Terms of Service</ModalHeader>
          <ModalCloseButton onClick={handleClose} />
          <ModalBody>
            <TOS />
          </ModalBody>
          <ModalFooter>
            <Button variant="primary" onClick={handleConfirmedClose}>
              I agree to the Terms of Service
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </GradientBox>
  )
}
